import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  combinedSearchThunk,
  loadingCombinedSearch,
  selectDrugResults,
  selectTopicQuery,
  selectTopicResults,
} from "../../../features/enhancedSearch";
import SearchResultItem from "./SearchResultItem";
import style from "./EnhancedSearchResults.module.scss";
import classNames from "classnames";
import PropTypes from "prop-types";
import { mapDrugs } from "./mapper/mapDrugs";
import Loader from "../../Loader/Loader";
import AppSearchFilters from "../AppSearchFilters/AppSearchFilters";
import AnimatedPopup from "../../AnimatedComponents/AnimatedPopup";

const EnhancedSearchResults = ({
  search,
  selectedTab,
  setSelectedTab,
  isAppMode,
  navigate,
}) => {
  const dispatch = useDispatch();
  const previousSearchQuery = useSelector(selectTopicQuery);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [skipLoading, setSkipLoading] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    if (searchParams.has("appendedinputbuttons")) {
      setSkipLoading(false);
      const urlSearch = searchParams.get("appendedinputbuttons");
      const guidelineFilter = searchParams.getAll("guidelinefilter");
      const searchWithin = searchParams.get("searchWithin");
      setSearchTerm(urlSearch);
      dispatch(
        combinedSearchThunk({
          searchText: urlSearch,
          guidelineFacet: guidelineFilter,
          top: previousSearchQuery?.top,
          searchWithin: searchWithin || "",
        }),
      );
    }
  }, [search]);

  const drugResults = useSelector(selectDrugResults);
  const isLoading = useSelector(loadingCombinedSearch);
  const remappedDrugs = mapDrugs(drugResults);
  const remappedDrugResults = { results: remappedDrugs };
  const searchResults = [useSelector(selectTopicResults), remappedDrugResults];
  const noResultsMessage = selectedTab === 0 ? "topics" : "drug indications";
  const resultCount =
    selectedTab === 0
      ? searchResults[selectedTab]?.totalCount
      : searchResults[selectedTab]?.results?.length;

  const loadMore = () => {
    const searchParams = new URLSearchParams(search);
    const guidelineFilter = searchParams.get("guidelinefilter");
    const searchWithin = searchParams.get("searchWithin");
    setSkipLoading(true);
    dispatch(
      combinedSearchThunk({
        searchText: previousSearchQuery.searchText,
        guidelineFacet: guidelineFilter,
        top: previousSearchQuery?.top ? previousSearchQuery.top : 10,
        skip: previousSearchQuery?.skip ? previousSearchQuery?.skip + 10 : 10,
        previousResults: searchResults[0].results,
        searchWithin: searchWithin,
      }),
    );
  };

  return (
    <div
      className={classNames(
        style.searchPage,
        "col-md-8 columnBlockLayout tabContainer regular",
      )}
    >
      <div
        className={classNames(
          { "size-20": isAppMode !== true, "size-16": isAppMode === true },
          style.tabSection,
        )}
      >
        <button
          className={classNames({ [style["active"]]: selectedTab === 0 })}
          onClick={() => setSelectedTab(0)}
        >
          Topic
          {/*{!isLoading && selectedTab === 0 && (*/}
          {/*  <div className={style.resultCountUnder}>*/}
          {/*    {resultCount ?? 0} results*/}
          {/*  </div>*/}
          {/*)}*/}
        </button>
        <button
          className={classNames({ [style["active"]]: selectedTab === 1 })}
          onClick={() => setSelectedTab(1)}
        >
          {isAppMode ? "Drug" : "Drug indication"}
          {/*{!isLoading && selectedTab === 1 && (*/}
          {/*  <div className={style.resultCountUnder}>*/}
          {/*    {resultCount ?? 0} results*/}
          {/*  </div>*/}
          {/*)}*/}
        </button>
        <div
          className={classNames(
            {
              "size-16": isAppMode !== true,
              "size-12 regular": isAppMode === true,
            },
            style.resultCount,
          )}
        >
          {!isLoading && <span>{resultCount ?? 0} results</span>}
          {isAppMode === true && (
            <button type="button" onClick={() => setShowFilters(true)}>
              <i className="tg-icons-Filters size-14"></i>
            </button>
          )}
        </div>
      </div>
      <ul className={style.resultList}>
        {isLoading && !skipLoading ? (
          <div className={style.loader}>
            <Loader />
          </div>
        ) : searchResults[selectedTab] &&
          searchResults[selectedTab].results?.length > 0 ? (
          searchResults[selectedTab].results.map((result, index) => (
            <SearchResultItem
              searchTerm={searchTerm}
              searchResult={result}
              key={index}
              isAppMode={isAppMode}
            />
          ))
        ) : (
          <div className={classNames("size-18", style.noResultsText)}>
            {`No ${noResultsMessage} found.`}
          </div>
        )}
      </ul>
      {isAppMode === true &&
        !isLoading &&
        resultCount > searchResults[selectedTab].results?.length && (
          <button
            className={classNames("size-14 medium", style.loadMore)}
            onClick={loadMore}
            data-testid="load-more"
          >
            <i
              className={classNames("tg-icons-ChevronDown", style.loadMoreIcon)}
            ></i>
            More search results
          </button>
        )}
      {isAppMode === true && (
        <AnimatedPopup show={showFilters}>
          <AppSearchFilters
            setShowFilters={setShowFilters}
            search={search}
            navigate={navigate}
          />
        </AnimatedPopup>
      )}
    </div>
  );
};

EnhancedSearchResults.propTypes = {
  search: PropTypes.string,
  isAppMode: PropTypes.bool,
  selectedTab: PropTypes.number,
  setSelectedTab: PropTypes.func,
};

export default EnhancedSearchResults;
